import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ChevronLeftIcon,
  SparklesIcon,
  XMarkIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { endpoints, urlBuilders } from '../config/endpoints';

interface DocMeta {
  category: string;
  slug: string;
  title: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  coach: 'Coach Guides',
  client: 'Client Guides',
  admin: 'Admin Guide',
};

const CATEGORY_ORDER = ['coach', 'client', 'admin'];

const HelpLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ href, children, ...props }) => {
  if (href && !href.startsWith('http') && !href.startsWith('mailto')) {
    return <Link to={`/help/${href}`} {...props as any}>{children}</Link>;
  }
  return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
};

const HelpPage: React.FC = () => {
  const { category, slug } = useParams<{ category?: string; slug?: string }>();
  const token = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType');

  const dashboardPath =
    userType === 'coach' ? '/dashboard/coach' : userType === 'client' ? '/dashboard/client' : '/';

  const [docs, setDocs] = useState<DocMeta[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);

  const [articleContent, setArticleContent] = useState<string | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);

  const [askInput, setAskInput] = useState('');
  const [aiAnswer, setAiAnswer] = useState<string | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const askInputRef = useRef<HTMLTextAreaElement>(null);

  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // Load doc list on mount
  useEffect(() => {
    const params = userType ? `?user_type=${userType}` : '';
    fetch(urlBuilders.main(endpoints.main.help.docs) + params, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then((data: DocMeta[]) => setDocs(Array.isArray(data) ? data : []))
      .catch(() => setDocs([]))
      .finally(() => setDocsLoading(false));
  }, [token, userType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load article content when route params change
  useEffect(() => {
    if (!category || !slug) {
      setArticleContent(null);
      return;
    }
    setArticleLoading(true);
    setAiAnswer(null);
    fetch(urlBuilders.main(endpoints.main.help.doc(category, slug)), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then((data: { content: string; title: string }) => {
        setArticleContent(data.content);
      })
      .catch(() => {
        setArticleContent(null);
      })
      .finally(() => setArticleLoading(false));
  }, [category, slug, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAsk = async () => {
    const question = askInput.trim();
    if (!question || isAsking) return;
    setIsAsking(true);
    setAiAnswer(null);
    setAskError(null);
    try {
      const res = await fetch(urlBuilders.main(endpoints.main.help.ask), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question, user_type: userType }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAiAnswer(data.answer as string);
    } catch {
      setAskError('Unable to get an answer right now. Try again or contact support@coachsightecr.com.');
    } finally {
      setIsAsking(false);
    }
  };

  const clearAnswer = () => { setAiAnswer(null); setAskInput(''); setAskError(null); };

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // Group docs by category, in defined order
  const grouped = CATEGORY_ORDER.reduce<Record<string, DocMeta[]>>((acc, cat) => {
    const catDocs = docs.filter(d => d.category === cat);
    if (catDocs.length) acc[cat] = catDocs;
    return acc;
  }, {});

  const activeKey = category && slug ? `${category}/${slug}` : null;

  return (
    <div className="min-h-screen bg-dark-bg text-gray-200 flex flex-col">
      <header className="flex items-center gap-4 px-6 py-4 border-b border-dark-border bg-dark-card flex-shrink-0">
        <Link to={dashboardPath} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors">
          <ChevronLeftIcon className="w-4 h-4" />
          Back to dashboard
        </Link>
        <span className="text-dark-border">|</span>
        <span className="text-sm font-semibold text-white">Help &amp; Guides</span>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-60 flex-shrink-0 border-r border-dark-border bg-dark-card overflow-y-auto py-4 px-2">
          {docsLoading ? (
            <div className="px-3 py-2 text-xs text-gray-500">Loading...</div>
          ) : (
            Object.entries(grouped).map(([cat, catDocs]) => {
              const collapsed = collapsedCategories.has(cat);
              return (
                <div key={cat} className="mb-3">
                  <button
                    onClick={() => toggleCategory(cat)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hover:text-gray-200 transition-colors"
                  >
                    {CATEGORY_LABELS[cat] ?? cat}
                    <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                  </button>
                  {!collapsed && (
                    <ul className="mt-0.5 space-y-0.5">
                      {catDocs.map(doc => {
                        const key = `${doc.category}/${doc.slug}`;
                        const isActive = key === activeKey;
                        return (
                          <li key={key}>
                            <Link
                              to={`/help/${doc.category}/${doc.slug}`}
                              className={`block px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                isActive
                                  ? 'bg-primary-600/20 text-primary-300 font-medium'
                                  : 'text-gray-400 hover:text-gray-200 hover:bg-dark-hover'
                              }`}
                            >
                              {doc.title}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          {/* AI search bar — always at top */}
          <div className="sticky top-0 z-10 bg-dark-bg border-b border-dark-border px-8 py-3">
            <div className="max-w-2xl flex gap-2 items-start">
              <div className="flex items-center gap-1.5 pt-2 flex-shrink-0">
                <SparklesIcon className="w-4 h-4 text-primary-400" />
                <span className="text-xs text-gray-400 whitespace-nowrap">Ask AI:</span>
              </div>
              <textarea
                ref={askInputRef}
                value={askInput}
                onChange={e => setAskInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
                placeholder="Type a question and press Enter..."
                rows={1}
                className="flex-1 resize-none rounded-lg bg-dark-card border border-dark-border text-sm text-gray-200 placeholder-gray-500 px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                onClick={handleAsk}
                disabled={!askInput.trim() || isAsking}
                className="px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex-shrink-0 flex items-center gap-1"
              >
                {isAsking
                  ? <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  : 'Ask'}
              </button>
            </div>
          </div>

          <div className="px-10 py-8 max-w-3xl">
            {/* AI answer */}
            {(aiAnswer || askError) && (
              <div className="mb-8 rounded-xl border border-primary-500/30 bg-primary-500/5 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="w-4 h-4 text-primary-400" />
                    <span className="text-sm font-medium text-primary-300">Answer</span>
                  </div>
                  <button onClick={clearAnswer} className="text-gray-500 hover:text-gray-300 transition-colors" title="Clear answer">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                {askError ? (
                  <p className="text-sm text-red-400">{askError}</p>
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none
                    prose-p:text-gray-300 prose-p:leading-relaxed
                    prose-strong:text-white
                    prose-ul:text-gray-300 prose-ol:text-gray-300 prose-li:my-0.5
                    prose-a:text-primary-300
                    prose-table:text-sm prose-th:text-gray-200 prose-th:font-semibold prose-td:text-gray-300
                    prose-code:text-primary-300 prose-code:bg-dark-card prose-code:px-1 prose-code:rounded">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: HelpLink }}>
                      {aiAnswer ?? ''}
                    </ReactMarkdown>
                  </div>
                )}
                <p className="mt-3 text-xs text-gray-600">
                  Generated from CoachSight ECR help docs. Contact{' '}
                  <a href="mailto:support@coachsightecr.com" className="text-primary-400 hover:underline">
                    support@coachsightecr.com
                  </a>{' '}
                  for further help.
                </p>
              </div>
            )}

            {/* Article or landing */}
            {articleLoading && (
              <div className="text-sm text-gray-500">Loading article...</div>
            )}

            {!articleLoading && articleContent && (
              <div className="prose prose-invert prose-sm max-w-none
                prose-h1:text-xl prose-h1:font-bold prose-h1:text-white prose-h1:mb-4
                prose-h2:text-base prose-h2:font-semibold prose-h2:text-gray-100 prose-h2:mt-6 prose-h2:mb-2
                prose-h3:text-sm prose-h3:font-semibold prose-h3:text-gray-200 prose-h3:mt-4 prose-h3:mb-1
                prose-p:text-gray-300 prose-p:leading-relaxed
                prose-strong:text-white
                prose-ul:text-gray-300 prose-ol:text-gray-300 prose-li:my-0.5
                prose-a:text-primary-300 hover:prose-a:text-primary-200
                prose-table:text-sm prose-table:w-full
                prose-th:text-gray-200 prose-th:font-semibold prose-th:bg-dark-card prose-th:px-3 prose-th:py-2
                prose-td:text-gray-300 prose-td:px-3 prose-td:py-1.5
                prose-hr:border-dark-border
                prose-code:text-primary-300 prose-code:bg-dark-card prose-code:px-1 prose-code:rounded">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: HelpLink }}>
                  {articleContent}
                </ReactMarkdown>
              </div>
            )}

            {!articleLoading && !articleContent && !category && (
              <div className="text-gray-400">
                <p className="text-base font-medium text-gray-200 mb-2">Welcome to CoachSight Help</p>
                <p className="text-sm">Use the search bar above to ask a question, or browse the guides in the left panel.</p>
              </div>
            )}

            {!articleLoading && !articleContent && category && (
              <div className="text-sm text-gray-500">Article not found. Try searching above.</div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default HelpPage;
